import { Actions, Async, FSpread } from '@client/new/helpers/types';
import { useUpdate } from '@client/new/helpers/useUpdate';
import _ from 'lodash';
import { UploadMap, Video } from './videos.domain';
import { uploadFile } from '../../creator/library/models/upload.hooks';
import { useEffect, useState } from 'react';
import { useUpload, VideoById } from '@client/new/common/videos/useUpload';
import { loadVideo } from '@client/new/helpers/load-video-from-file';
import { trackEvent } from '@services/tracking';
import toast from 'react-hot-toast';
import { ContentFilters } from '@client/new/common/library/helpers';
import { Media } from './workout.domain';

type PayloadMap = {
  onDrop: { file: File; videoType: ContentFilters | null };
  upload: never;
  editName: string;
  thumbnailTimestamp: number;
  cancel: never;
};
export type VideosModelAction = Actions<PayloadMap>;
export type UploadToLibraryModelDispatcher = FSpread<PayloadMap>;
export type UploadStep = 'Name' | 'Thumbnail' | 'Uploading' | 'Processing';

type VideosVMState = {
  uploadFile: File | null;
  thumbnailTimestamp: number;
  isUploading: boolean;
  isProcessing: boolean;
  isUploadFinished: boolean;
  uploadedId: string | null;
  validationError?: { title: string; description: string };
};

const initialState: VideosVMState = {
  uploadFile: null,
  thumbnailTimestamp: 0,
  isUploading: false,
  uploadedId: null,
  isProcessing: false,
  isUploadFinished: false,
  validationError: undefined,
};

export type VideoListItem = {
  id: string;
  imageUrl: string;
  title: string;
  duration: string;
  tag: string;
  date: string;
  onClick: (v: Video) => void;
};

export type UploadToLibraryModel = {
  dispatch: UploadToLibraryModelDispatcher;
  uploadFile: File | null;
  progress: number;
} & Pick<
  VideosVMState,
  | 'thumbnailTimestamp'
  | 'isUploading'
  | 'isProcessing'
  | 'isUploadFinished'
  | 'validationError'
>;

const singleFileId = '1';

const getUploadValidationError = (type: ContentFilters, duration: number) => {
  switch (type) {
    case 'short':
      return duration < 60
        ? undefined
        : {
            title: 'Video too long',
            description:
              'Your video file is longer than 60seconds. Short form videos have to be less than 60 sec.',
          };
    case 'long':
      return duration > 60
        ? undefined
        : {
            title: 'Video too short',
            description:
              'Your video file is shorter than 60seconds. Long form videos have to be more than 60 sec.',
          };
  }
};

export const useUploadToLibraryModel = (p: {
  influencerId: number;
  type: string | null;
  onAddVideo: (video: {
    uuid: string;
    title: string;
    thumbnailTimestampMs: number;
  }) => void;
  skipProcessing?: boolean;
}): UploadToLibraryModel => {
  const [state, updateState] = useUpdate(initialState);
  const [uploadMap, setUploadMap] = useState<UploadMap>({});

  const { uploadFinished, startPolling } = useUpload({
    skipProcessing: p.skipProcessing,
    influencerId: p.influencerId,
    uploadedId: state.uploadedId,
    addVideo: (v) => {
      if (!v) throw new Error('No video returned from upload');
      p.onAddVideo({
        uuid: v.uuid,
        title: v.title,
        thumbnailTimestampMs: state.thumbnailTimestamp * 1000,
      });
    },
  });

  useEffect(() => {
    if (uploadFinished && state.uploadedId) {
      updateState({ isUploadFinished: true, uploadedId: null });
    }
  }, [state.uploadedId, updateState, uploadFinished]);

  const dispatch: UploadToLibraryModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as VideosModelAction;
    switch (action.type) {
      case 'onDrop': {
        onDrop(action.payload);
        return null;
      }
      case 'upload':
        upload();
        return null;
      case 'cancel': {
        if (uploadMap[singleFileId]) {
          uploadMap[singleFileId]?.muxInstance &&
            uploadMap[singleFileId].muxInstance.abort();
        }
        updateState(initialState);
        return null;
      }
      case 'editName': {
        if (state.uploadFile) {
          const file = new File([state.uploadFile], `${action.payload}`, {
            type: state.uploadFile.type,
          });
          updateState({ uploadFile: file });
        }
        return null;
      }
      case 'thumbnailTimestamp':
        return updateState({ thumbnailTimestamp: action.payload });
    }
  };

  const onDrop = ({
    file,
    videoType,
  }: {
    file: File;
    videoType: ContentFilters | null;
  }) => {
    updateState({ validationError: undefined });
    if (!videoType) {
      updateState({ uploadFile: file });
      return;
    }

    loadVideo(file).then((video) => {
      const validationError = getUploadValidationError(
        videoType,
        video.duration,
      );
      validationError
        ? updateState({ validationError })
        : updateState({ uploadFile: file });
    });
  };

  const onUploadSuccess = (uploadedId: string) => {
    updateState({ isProcessing: true, uploadedId });
    startPolling(3000);
  };

  const upload = async () => {
    updateState({ isUploading: true });
    try {
      if (!state.uploadFile) return;
      trackEvent({
        type: 'Video Upload Started',
        payload: { 'Number of Videos': 1 },
      });
      await uploadFile({
        video: {
          file: state.uploadFile,
          thumbnailTimestamp: state.thumbnailTimestamp,
          id: singleFileId,
        },
        influencerId: p.influencerId,
        setUploadMap,
        uploadMap,
        onSuccess: (uploadedId: string) => onUploadSuccess(uploadedId),
        folderId: null,
      });
    } catch (e) {
      toast.error('Something went wrong!');
    } finally {
      updateState({ isUploading: false });
    }
  };
  const progress = _.isEmpty(uploadMap) ? 0 : uploadMap[singleFileId].progress;

  return {
    dispatch,
    uploadFile: state.uploadFile,
    thumbnailTimestamp: state.thumbnailTimestamp,
    progress,
    isProcessing: state.isProcessing,
    isUploading: state.isUploading,
    isUploadFinished: state.isUploadFinished,
    validationError: state.validationError,
  };
};
